import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError, setToken } from "../api";
import { useAuth } from "../context/AuthContext";

export default function AuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [err, setErr] = useState("");
  const [stalled, setStalled] = useState(false);
  const ran = useRef(false);
  const watchdog = useRef(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    // Watchdog: never leave the user on an endless spinner if the handshake hangs.
    watchdog.current = setTimeout(() => {
      setErr((e) => e || "This is taking longer than expected. Your connection may have dropped.");
      setStalled(true);
    }, 15000);
    (async () => {
      try {
        const hash = window.location.hash || "";
        const m = hash.match(/session_id=([^&]+)/);
        if (!m) throw new Error("Missing session_id");
        const session_id = decodeURIComponent(m[1]);
        const { data } = await api.post("/auth/google/session", { session_id });
        if (data.access_token) setToken(data.access_token);
        // Remove hash so refresh doesn't retry
        window.history.replaceState({}, "", window.location.pathname);
        clearTimeout(watchdog.current);
        setUser(data.user);
        nav(`/${data.user.role}/dashboard`, { replace: true, state: { user: data.user } });
      } catch (e) {
        clearTimeout(watchdog.current);
        setErr(formatError(e) || e.message);
      }
    })();
    return () => clearTimeout(watchdog.current);
  }, [nav, setUser]);

  // Restart the Google handshake so a stalled/failed attempt can recover.
  const retryGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const failed = Boolean(err) || stalled;

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper" data-testid="auth-callback">
      <div className="card card-pad max-w-md text-center">
        <span className="eyebrow">Finishing sign-in</span>
        <div className="font-display font-bold text-2xl mt-3">{failed ? "Sign-in failed" : "Connecting your Google account…"}</div>
        {err && <p className="text-red-600 text-sm mt-3" data-testid="google-callback-error">{err}</p>}
        {failed && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button className="btn-primary w-full sm:w-auto" onClick={retryGoogle} data-testid="google-retry-btn">
              Try again
            </button>
            <button className="btn-secondary w-full sm:w-auto" onClick={() => nav("/login", { replace: true })} data-testid="google-back-to-login-btn">
              Back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
